/**
 * Module-scope LRU cache for the Agave coding dropdown hook.
 *
 * Extracted from `useAgaveCodingDropdown` so callers outside the hook (e.g.
 * the auth slice on company switch / sign out) can call
 * `invalidateAgaveOptionsCache()` without importing the hook itself, which
 * would create a circular dependency through the Redux store.
 */

export interface AgaveCodingCacheEntry {
  items: unknown[];
  nextToken: string | null;
  total: number | undefined;
  timestamp: number;
}

export const AGAVE_CACHE_TIME_TO_LIVE_MS = 5 * 60 * 1000;
export const AGAVE_CACHE_MAX_ENTRIES = 200;

export const agaveOptionsCache = new Map<string, AgaveCodingCacheEntry>();
export const agaveInflightRequests = new Map<string, Promise<AgaveCodingCacheEntry | null>>();

export function makeAgaveCacheKey(codingType: string, filter: Record<string, unknown>): string {
  return `${codingType}|${JSON.stringify(filter)}`;
}

// Map preserves insertion order, so delete+set on read moves an entry to "most
// recently used"; eviction drops the oldest (first-iterated) key when we overflow.
export function getAgaveCached(key: string): AgaveCodingCacheEntry | null {
  const entry = agaveOptionsCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > AGAVE_CACHE_TIME_TO_LIVE_MS) {
    agaveOptionsCache.delete(key);
    return null;
  }
  agaveOptionsCache.delete(key);
  agaveOptionsCache.set(key, entry);
  return entry;
}

export function setAgaveCached(key: string, entry: AgaveCodingCacheEntry): void {
  if (agaveOptionsCache.has(key)) agaveOptionsCache.delete(key);
  agaveOptionsCache.set(key, entry);
  if (agaveOptionsCache.size > AGAVE_CACHE_MAX_ENTRIES) {
    const oldestKey = agaveOptionsCache.keys().next().value;
    if (oldestKey !== undefined) agaveOptionsCache.delete(oldestKey);
  }
}

export function invalidateAgaveOptionsCache(partialKey?: string): void {
  if (!partialKey) {
    agaveOptionsCache.clear();
    return;
  }
  for (const key of agaveOptionsCache.keys()) {
    if (key.startsWith(partialKey)) {
      agaveOptionsCache.delete(key);
    }
  }
}
